import React, { memo, useMemo } from 'react';
import { Download, ExternalLink, FileAudio, FileImage, FileText } from 'lucide-react';
import { getBidiTextProps, ltrIsolateStyle } from './textDirection';

const INTERNAL_AUDIO_PROMPT = /^Please listen to the attached voice recording and respond to it\.?$/i;
const INTERNAL_AI_SYSTEM_PREFIX = /^AI System \(name: .*?, ID: .*?\)$/i;
const ATTACHMENT_HEADING = /^\*\*Attached (Files|Voice Recording):\*\*$/i;
const VIEW_LINK = /\s*\[View\]\(([^)]+)\)\s*$/i;

function parseAttachmentLine(line) {
  const viewMatch = line.match(VIEW_LINK);
  const fileUrl = viewMatch ? viewMatch[1] : '';
  const withoutLink = viewMatch ? line.replace(VIEW_LINK, '').trim() : line.trim();
  const match = withoutLink.match(/^-\s+(.+)\s+\(([^,]+),\s*([^)]+)\)\s*$/);
  if (!match) return null;

  return {
    name: match[1].trim(),
    size: match[2].trim(),
    type: match[3].trim().toUpperCase(),
    url: fileUrl,
  };
}

export function parseAttachmentMessage(content = '') {
  const lines = String(content || '').split('\n');
  const visibleLines = [];
  const attachments = [];
  let inAttachmentSection = false;

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed && inAttachmentSection) {
      continue;
    }

    if (INTERNAL_AUDIO_PROMPT.test(trimmed) || INTERNAL_AI_SYSTEM_PREFIX.test(trimmed)) {
      continue;
    }

    if (ATTACHMENT_HEADING.test(trimmed)) {
      inAttachmentSection = true;
      continue;
    }

    if (inAttachmentSection && trimmed.startsWith('- ')) {
      const attachment = parseAttachmentLine(trimmed);
      if (attachment) {
        attachments.push(attachment);
        continue;
      }
    }

    inAttachmentSection = false;
    visibleLines.push(line);
  }

  return {
    text: visibleLines.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    attachments,
  };
}

function isAudioAttachment(attachment) {
  return attachment.type === 'AUDIO' || /\.(webm|wav|mp3|m4a|ogg)$/i.test(attachment.name);
}

function isImageAttachment(attachment) {
  return attachment.type === 'IMAGE' || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(attachment.name);
}

function AttachmentIcon({ attachment }) {
  if (isAudioAttachment(attachment)) return <FileAudio size={18} />;
  if (isImageAttachment(attachment)) return <FileImage size={18} />;
  return <FileText size={18} />;
}

function AttachmentCard({ attachment }) {
  const hasUrl = Boolean(attachment.url);
  const isAudio = isAudioAttachment(attachment);
  const isImage = isImageAttachment(attachment);
  const nameBidiProps = getBidiTextProps(attachment.name);

  if (isAudio) {
    if (!hasUrl) return null;

    return (
      <audio
        controls
        src={attachment.url}
        aria-label="Audio message"
        style={{
          display: 'block',
          width: '100%',
          minWidth: 260,
          maxWidth: '100%',
        }}
      />
    );
  }

  return (
    <div
      style={{
        border: '1px solid rgba(148, 163, 184, 0.35)',
        borderRadius: 8,
        padding: 10,
        background: 'rgba(15, 23, 42, 0.10)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 220,
        maxWidth: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
          <AttachmentIcon attachment={attachment} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div {...nameBidiProps} style={{ ...nameBidiProps.style, fontWeight: 600, overflowWrap: 'anywhere' }}>{attachment.name}</div>
          <div dir="ltr" style={{ ...ltrIsolateStyle, opacity: 0.72, fontSize: 12 }}>{attachment.size} · {attachment.type}</div>
        </div>
      </div>

      {hasUrl && isImage && (
        <a href={attachment.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
          <img
            src={attachment.url}
            alt={attachment.name}
            style={{
              display: 'block',
              width: '100%',
              maxHeight: 220,
              objectFit: 'contain',
              borderRadius: 6,
              background: 'rgba(15, 23, 42, 0.18)',
            }}
          />
        </a>
      )}

      {hasUrl && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'inherit', textDecoration: 'underline' }}
          >
            <ExternalLink size={14} />
            Open
          </a>
          <a
            href={attachment.url}
            download={attachment.name}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'inherit', textDecoration: 'underline' }}
          >
            <Download size={14} />
            Download
          </a>
        </div>
      )}
    </div>
  );
}

const AttachmentMessage = memo(/** @param {{ content?: string, className?: string }} props */ function AttachmentMessage({ content, className = '' }) {
  const parsed = useMemo(() => parseAttachmentMessage(content), [content]);
  const textBidiProps = getBidiTextProps(parsed.text);

  if (!parsed.text && parsed.attachments.length === 0) {
    return null;
  }

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {parsed.text && (
        <div {...textBidiProps} style={{ ...textBidiProps.style, whiteSpace: 'pre-wrap' }}>
          {parsed.text}
        </div>
      )}
      {parsed.attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {parsed.attachments.map((attachment, index) => (
            <AttachmentCard key={`${attachment.name}-${index}`} attachment={attachment} />
          ))}
        </div>
      )}
    </div>
  );
});

export default AttachmentMessage;
